import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function LoginPage({ adminMode = false }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      if (adminMode && u.role !== "admin") {
        setError("Cet espace est réservé à l'administrateur.");
        setLoading(false);
        return;
      }
      toast.success("Connexion réussie");
      if (u.role === "admin") navigate("/admin");
      else if (u.status === "approved") {
        const to = location.state?.from || "/shop";
        navigate(to);
      } else navigate("/pending");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid={adminMode ? "admin-login-page" : "login-page"}>
      <div className="hidden md:flex md:w-1/2 bg-[#1A2B1E] relative items-center justify-center p-12">
        <img
          src="https://images.unsplash.com/photo-1488459716781-31db52582fe9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMHZlZ2V0YWJsZXMlMjBtYXJrZXR8ZW58MHx8fHwxNzgwNDkxMDU3fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="ff-grain" />
        <div className="relative text-white max-w-md">
          <p className="ff-overline text-[#F4A261]">Espace pro</p>
          <h2 className="font-serif text-5xl mt-4 leading-tight">
            Bienvenue chez<br />FineFood Primeur.
          </h2>
          <p className="mt-6 text-[#C9D2CC]">
            Connectez-vous pour passer commande, consulter vos factures et gérer votre compte.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md ff-fade-in">
          <Link to="/" className="inline-block mb-10">
            <img src={LOGO_URL} alt="FineFood Primeur" className="h-16" />
          </Link>
          <p className="ff-overline">{adminMode ? "Espace administrateur" : "Connexion pro"}</p>
          <h1 className="font-serif text-4xl mt-3 text-[#1A2B1E]">
            {adminMode ? "Accès restreint" : "Se connecter"}
          </h1>
          <p className="mt-3 text-[#4A5D4E]">
            {adminMode
              ? "Veuillez vous identifier pour gérer la plateforme."
              : "Saisissez vos identifiants pour accéder à votre compte."}
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ff-input mt-2"
                data-testid="login-email-input"
                placeholder="nom@restaurant.ch"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-[#4A5D4E]">Mot de passe</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="ff-input mt-2"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <div className="text-sm text-[#D62828] bg-[#D62828]/10 p-3 border border-[#D62828]/30" data-testid="login-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="ff-btn-primary w-full justify-center"
              data-testid="login-submit-button"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </form>

          {!adminMode && (
            <p className="mt-8 text-sm text-[#4A5D4E]">
              Pas encore de compte ?{" "}
              <Link to="/register" data-testid="login-register-link" className="text-[#2D6A4F] font-medium hover:underline">
                Créer un compte professionnel
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
